import { css } from '@emotion/react'
import { HFlow, TableFooter, VFlow } from 'bold-ui'
import { AccordionDataTable } from 'components/accordion/accordion-data-table/AccordionDataTable'
import { useAccordionControl } from 'components/accordion/useAccordionControl'
import { DateTime } from 'components/date'
import { HLabel } from 'components/HLabel'
import { TableBox } from 'components/table'
import { usePagination } from 'components/table/usePagination'
import { addDays } from 'date-fns'
import { useCompartilhamentoCuidadoPlanoQuery } from 'graphql/hooks.generated'
import { CompartilhamentoCuidadoPlanoDto, TipoAcompanhamentoEnum } from 'graphql/types.generated'
import { useMemo } from 'react'
import StatusSquare from 'view/atendimentos/detail/historico/components/StatusSquare'
import { CompartilhamentoCuidadoTablePanel } from 'view/atendimentos/detail/soap/plano/compartilhamento-cuidado/compartilhamento-cuidado-table/CompartilhamentoCuidadoTablePanel'
import { CompartilhamentoCuidadoPlanoModel } from 'view/atendimentos/detail/soap/plano/compartilhamento-cuidado/model-compartilhamentocuidado'
import { convertValorHistoricoToJoined } from 'view/atendimentos/detail/soap/plano/compartilhamento-cuidado/util-compartilhamentocuidado'
import {
  cuidadoCompartilhadoClassificacaoPrioridadeRecord,
  TipoCompartilhamentoCuidadoPlanoEnum,
} from 'view/atendimentos/detail/soap/plano/cuidado-compartilhado/model-cuidadocompartilhado'
import { formatNomeProfissional } from 'view/profissional/utils/utils-profissional'

import { AcompanhamentoCard } from '../../components/AcompanhamentoCard'
import { DIAS_GESTACAO_LIMITE } from '../../puerperio/model-acompanhamentoPuerperio'

interface EncaminhamentosGestacaoCardProps {
  prontuarioId: ID
  tipoAcompanhamento: TipoAcompanhamentoEnum
  dataInicioCondicao: Date
  dataFimPuerperio?: Date
}

export function EncaminhamentosGestacaoCard(props: Readonly<EncaminhamentosGestacaoCardProps>) {
  const { prontuarioId, tipoAcompanhamento, dataInicioCondicao, dataFimPuerperio } = props

  const { data: { compartilhamentosCuidadoPlano } = {}, loading } = useCompartilhamentoCuidadoPlanoQuery({
    variables: {
      compartilhamentoCuidadoPlanoQueryInput: {
        prontuarioId,
      },
    },
    fetchPolicy: 'no-cache',
  })

  const { resetExpandedItems, ...accordionProps } = useAccordionControl({})

  const dataLimiteGestacao =
    tipoAcompanhamento === TipoAcompanhamentoEnum.PRE_NATAL
      ? addDays(dataInicioCondicao, DIAS_GESTACAO_LIMITE)
      : undefined

  const filteredItems = useMemo(() => {
    const dataLimite = tipoAcompanhamento === TipoAcompanhamentoEnum.PRE_NATAL ? dataLimiteGestacao : dataFimPuerperio

    return ((compartilhamentosCuidadoPlano as CompartilhamentoCuidadoPlanoDto[]) ?? [])
      .map(convertValorHistoricoToJoined)
      .filter((item) => {
        if (!item.iniciadoEm) return false

        const dataItem = new Date(item.iniciadoEm)
        const dataInicio = new Date(dataInicioCondicao)

        const isMaiorOuIgualInicio = dataItem >= dataInicio

        const isMenorOuIgualLimite = dataLimite ? dataItem <= new Date(dataLimite) : true

        return isMaiorOuIgualInicio && isMenorOuIgualLimite
      })
  }, [compartilhamentosCuidadoPlano, dataInicioCondicao, dataFimPuerperio, dataLimiteGestacao, tipoAcompanhamento])

  const { paginatedItems, tableProps } = usePagination<CompartilhamentoCuidadoPlanoModel>({
    items: filteredItems,
    initialPageSize: 8,
    onChange: resetExpandedItems,
  })

  return (
    <AcompanhamentoCard
      id='encaminhamentos-compartilhamento-cuidado'
      title='Encaminhamento/Compartilhamento de cuidado'
      tipoAcompanhamento={tipoAcompanhamento}
      loading={loading}
      placeholder='Nenhum encaminhamento registrado'
      justifyHeaderContent='space-between'
    >
      <VFlow vSpacing={0}>
        <TableBox>
          <AccordionDataTable
            {...accordionProps}
            rows={paginatedItems}
            columns={[
              {
                name: 'data',
                header: 'Data',
                render: (row: CompartilhamentoCuidadoPlanoModel) => (
                  <HFlow hSpacing={0.5} alignItems='center'>
                    <DateTime format='DD/MM/YYYY' value={row.iniciadoEm} />
                  </HFlow>
                ),
                size: 2,
              },
              {
                name: 'prioridade',
                header: 'Prioridade',
                render: (row: CompartilhamentoCuidadoPlanoModel) => {
                  const { label, mainColor } = cuidadoCompartilhadoClassificacaoPrioridadeRecord[row.prioridade]
                  return (
                    <HFlow justifyContent='center'>
                      <StatusSquare color={mainColor} description={label} descriptionAsTooltip />
                    </HFlow>
                  )
                },
                size: 2,
                style: css`
                  text-align: center;
                `,
              },
              {
                name: 'tipoCompartilhamento',
                header: 'Tipo de compartilhamento',
                render: (row: CompartilhamentoCuidadoPlanoModel) =>
                  row.tipo === TipoCompartilhamentoCuidadoPlanoEnum.CUIDADO_COMPARTILHADO ? (
                    <VFlow vSpacing={0}>
                      <HLabel title='Cuidado compartilhado:'>
                        {row.lotacaoExecutante?.profissional
                          ? `${formatNomeProfissional(
                              row.lotacaoExecutante.profissional
                            )} - ${row.cbo.nome.capitalize()}`
                          : row.cbo.nome.capitalize()}
                      </HLabel>
                      <HLabel title='Unidade de saúde:'>{row.unidadeSaude?.nome}</HLabel>
                    </VFlow>
                  ) : (
                    <HLabel title='Guia de encaminhamento:'>{row.especialidade?.nome.capitalize()}</HLabel>
                  ),
                size: 8,
                style: css`
                  flex-grow: 1;
                `,
              },
            ]}
            components={{ AccordionPanel: CompartilhamentoCuidadoTablePanel }}
            loading={loading}
          />
          <TableFooter {...tableProps} style={styles.tableFooter} />
        </TableBox>
      </VFlow>
    </AcompanhamentoCard>
  )
}

const styles = {
  tableFooter: css`
    border-top-width: 0;
  `,
}
